import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, Check, X, ChevronDown, Clock, 
  CheckCircle, XCircle, Plus, User
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { getAllDeposits, updateDeposit, manualDeposit, getAllUsers } from '../services/api';
import { toast } from 'sonner';

const AdminDepositsPage = () => {
  const [deposits, setDeposits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualData, setManualData] = useState({ userId: '', amount: '', cryptoType: 'USDT' });
  const [adminNotes, setAdminNotes] = useState('');
  const [selectedDeposit, setSelectedDeposit] = useState(null);

  useEffect(() => {
    fetchDeposits();
    fetchUsers();
  }, [statusFilter]);

  const fetchDeposits = async () => {
    try {
      const response = await getAllDeposits(statusFilter || undefined);
      setDeposits(response.data);
    } catch (error) {
      toast.error('Failed to load deposits');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getAllUsers({ status: 'active' });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleApprove = async (depositId) => {
    try {
      await updateDeposit(depositId, { status: 'approved', admin_notes: adminNotes });
      toast.success('Deposit approved and balance updated');
      setSelectedDeposit(null);
      setAdminNotes('');
      fetchDeposits();
    } catch (error) {
      toast.error('Failed to approve deposit');
    }
  };

  const handleReject = async (depositId) => {
    try {
      await updateDeposit(depositId, { status: 'rejected', admin_notes: adminNotes });
      toast.success('Deposit rejected');
      setSelectedDeposit(null);
      setAdminNotes('');
      fetchDeposits();
    } catch (error) {
      toast.error('Failed to reject deposit');
    }
  };

  const handleManualDeposit = async (e) => {
    e.preventDefault();
    try {
      await manualDeposit(manualData.userId, parseFloat(manualData.amount), manualData.cryptoType);
      toast.success('Manual deposit added successfully');
      setShowManualModal(false);
      setManualData({ userId: '', amount: '', cryptoType: 'USDT' });
      fetchDeposits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add deposit');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="badge badge-success"><CheckCircle className="w-3 h-3 mr-1" />Approved</span>;
      case 'rejected':
        return <span className="badge badge-danger"><XCircle className="w-3 h-3 mr-1" />Rejected</span>;
      default:
        return <span className="badge badge-warning"><Clock className="w-3 h-3 mr-1" />Pending</span>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-deposits-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="admin-page-header">
            <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white">
              Deposit Management
            </h1>
            <p className="text-slate-400">Review and approve user deposits</p>
          </div>
          <button
            onClick={() => setShowManualModal(true)}
            className="btn-primary flex items-center gap-2 min-h-[48px]"
            data-testid="manual-deposit-btn"
          >
            <Plus className="w-4 h-4" />
            Manual Deposit
          </button>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-filters"
        >
          <div className="filter-select relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field pr-10 appearance-none cursor-pointer w-full"
              data-testid="filter-deposits"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </motion.div>

        {/* Deposits Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Crypto</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan="6"><div className="skeleton h-12" /></td>
                    </tr>
                  ))
                ) : deposits.length > 0 ? (
                  deposits.map((deposit) => (
                    <tr key={deposit.id} data-testid={`deposit-row-${deposit.id}`}>
                      <td>
                        <div>
                          <p className="text-white font-medium">{deposit.user?.name}</p>
                          <p className="text-slate-400 text-sm">{deposit.user?.email}</p>
                        </div>
                      </td>
                      <td>
                        <p className="text-white font-semibold text-lg">{formatCurrency(deposit.amount)}</p>
                      </td>
                      <td>
                        <span className="badge badge-info">{deposit.crypto_type}</span>
                      </td>
                      <td>{getStatusBadge(deposit.status)}</td>
                      <td className="text-slate-400">{formatDate(deposit.created_at)}</td>
                      <td>
                        {deposit.status === 'pending' && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => { setSelectedDeposit(deposit); }}
                              className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                              title="Review"
                              data-testid={`review-${deposit.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        {deposit.admin_notes && (
                          <p className="text-xs text-slate-500 mt-1">{deposit.admin_notes}</p>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-12">
                      <Wallet className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                      <p className="text-slate-400">No deposits found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Review Deposit Modal */}
      <AnimatePresence>
        {selectedDeposit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-overlay"
            onClick={() => setSelectedDeposit(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="review-modal"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Review Deposit</h2>
              
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-slate-400 text-sm">User</p>
                  <p className="text-white font-semibold">{selectedDeposit.user?.name}</p>
                  <p className="text-slate-400 text-sm">{selectedDeposit.user?.email}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-slate-400 text-sm">Amount</p>
                  <p className="text-2xl font-bold text-emerald-400">{formatCurrency(selectedDeposit.amount)}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-slate-400 text-sm">Cryptocurrency</p>
                  <p className="text-white font-semibold">{selectedDeposit.crypto_type}</p>
                </div>
                {selectedDeposit.proof_image && (
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-slate-400 text-sm mb-2">Payment Proof</p>
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${selectedDeposit.proof_image}`}
                      alt="Payment proof"
                      className="rounded-lg max-h-48 object-contain"
                    />
                  </div>
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">Admin Notes (Optional)</label>
                <input
                  type="text"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="input-field"
                  placeholder="Add a note..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => handleReject(selectedDeposit.id)}
                  className="btn-secondary flex-1 flex items-center justify-center gap-2 text-red-400 border-red-500/20"
                  data-testid="reject-deposit"
                >
                  <X className="w-4 h-4" />
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(selectedDeposit.id)}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="approve-deposit"
                >
                  <Check className="w-4 h-4" />
                  Approve
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual Deposit Modal */}
      <AnimatePresence>
        {showManualModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-overlay"
            onClick={() => setShowManualModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="manual-deposit-modal"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Manual Deposit</h2>
              
              <form onSubmit={handleManualDeposit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Select User</label>
                  <select
                    value={manualData.userId}
                    onChange={(e) => setManualData({ ...manualData, userId: e.target.value })}
                    className="input-field"
                    required
                    data-testid="manual-user-select"
                  >
                    <option value="">Select a user...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Amount (USD)</label>
                  <input
                    type="number"
                    value={manualData.amount}
                    onChange={(e) => setManualData({ ...manualData, amount: e.target.value })}
                    className="input-field"
                    placeholder="0.00"
                    min="1"
                    step="0.01"
                    required
                    data-testid="manual-amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Cryptocurrency</label>
                  <select
                    value={manualData.cryptoType}
                    onChange={(e) => setManualData({ ...manualData, cryptoType: e.target.value })}
                    className="input-field"
                  >
                    <option value="BTC">BTC</option>
                    <option value="ETH">ETH</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>

                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={() => setShowManualModal(false)} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary flex-1" data-testid="submit-manual">
                    Add Deposit
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default AdminDepositsPage;
